import { useEffect, useState, type ReactNode } from 'react'
import { ApiError } from '@/api/errors'
import { Button } from './Button'
import { Textarea, Label } from './Field'
import { cn } from './cn'

/** Bottom sheet primitive. Mobile-first, dismissible, accessible. */
export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-tg-bg p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-black/15" aria-hidden />
        {title && <h2 className="mb-2 text-lg font-semibold text-tg-text">{title}</h2>}
        {children}
      </div>
    </div>
  )
}

export interface ReasonConfig {
  label: string
  required?: boolean
  placeholder?: string
}

/** Confirm a critical action, optionally capturing a reason/comment. */
export function ConfirmSheet({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  confirmVariant = 'primary',
  loading = false,
  error,
  reason,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  confirmLabel: string
  confirmVariant?: 'primary' | 'danger'
  loading?: boolean
  error?: unknown
  reason?: ReasonConfig
  onConfirm: (reason: string) => void
}) {
  const [value, setValue] = useState('')

  // Reset captured reason whenever the sheet opens.
  useEffect(() => {
    if (open) setValue('')
  }, [open])

  const reasonMissing = !!reason?.required && value.trim().length === 0
  const errMessage =
    error instanceof ApiError ? error.message : error instanceof Error ? error.message : null

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      {description && <p className="mb-3 text-sm text-tg-hint">{description}</p>}
      {reason && (
        <div className="mb-3">
          <Label>
            {reason.label}
            {reason.required && <span className="text-risk-red"> *</span>}
          </Label>
          <Textarea
            value={value}
            placeholder={reason.placeholder}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
          />
        </div>
      )}
      {errMessage && (
        <p className={cn('mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-risk-red')}>
          {errMessage}
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" size="lg" fullWidth onClick={onClose} disabled={loading}>
          Отмена
        </Button>
        <Button
          variant={confirmVariant}
          size="lg"
          fullWidth
          loading={loading}
          disabled={reasonMissing}
          onClick={() => onConfirm(value.trim())}
        >
          {confirmLabel}
        </Button>
      </div>
    </Sheet>
  )
}
